// clean-social-handle.util.ts
export type SocialPlatform =
  | 'facebook'
  | 'instagram'
  | 'twitter'
  | 'x'
  | 'tiktok'
  | 'youtube'
  | 'pinterest'
  | 'website'
  | 'rss';

export function cleanSocialHandle(raw: string, platform?: SocialPlatform): string {
  if (!raw) return '';

  let url = raw.trim();

  // If the user just typed a handle (@name or name)
  const looksLikePlainHandle =
    !url.includes('http') && !url.includes('www.') && !url.includes('/') && !url.includes('?');

  if (looksLikePlainHandle) {
    // Normalize: for tiktok & youtube we prefer @handle style, others plain
    if ((platform === 'tiktok' || platform === 'youtube') && !url.startsWith('@')) {
      return '@' + url;
    }
    return url;
  }

  // Try to parse as URL
  let parsed: URL | null = null;
  try {
    parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
  } catch {
    // Fallback: last path segment
    const last = url.split('/').filter(Boolean).pop() || '';
    if ((platform === 'tiktok' || platform === 'youtube') && !last.startsWith('@')) {
      return '@' + last;
    }
    return last;
  }

  const hostname = parsed.hostname.replace(/^www\./, '');
  const path = parsed.pathname.replace(/\/+$/, '');
  const segments = path.split('/').filter(Boolean);

  // Auto-detect if platform not provided
  if (!platform) {
    if (hostname.includes('tiktok.com')) platform = 'tiktok';
    else if (hostname.includes('instagram.com')) platform = 'instagram';
    else if (hostname.includes('facebook.com')) platform = 'facebook';
    else if (hostname.includes('x.com') || hostname.includes('twitter.com')) platform = 'twitter';
    else if (hostname.includes('youtube.com') || hostname === 'youtu.be') platform = 'youtube';
  }

  switch (platform) {
    case 'tiktok': {
      const handle = segments[0] || '';
      return handle.startsWith('@') ? handle : '@' + handle;
    }

    case 'instagram':
    case 'twitter':
    case 'x':
    case 'facebook':
    case 'pinterest': {
      return segments[0] || '';
    }

    case 'youtube': {
      const first = segments[0];

      if (hostname === 'youtu.be') {
        return first;
      }

      if (first === 'user' || first === 'channel') {
        return segments[1] || '';
      }

      if (first?.startsWith('@')) return first;
      return first || '';
    }

    case 'website':
    case 'rss': {
      // For website / RSS you probably want full URL, not handle
      return url;
    }

    default: {
      // Generic fallback: just last path segment
      return segments.pop() || '';
    }
  }
}
